import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Calendar, Clock, MapPin, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY } from "../lib/constants";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      const bid = params.get("booking_id");
      if (bid) {
        api.get(`/bookings/${bid}`).then((r) => { setBooking(r.data); setState("paid"); }).catch(() => setState("error"));
      } else { setState("error"); }
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setBooking(data.booking); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 8) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-[#f8f9fa] min-h-[70vh] flex items-center" data-testid="payment-success-page">
      <div className="ubm-container py-20 max-w-xl mx-auto text-center">
        {state === "checking" && (
          <div><Loader2 className="animate-spin text-primary mx-auto mb-4" size={44} /><p className="text-charcoal-600">Confirming your payment…</p></div>
        )}
        {state === "error" && (
          <div className="bg-white border border-gray-200 rounded-md p-10">
            <XCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h1 className="text-2xl font-bold text-charcoal-900">We couldn't confirm your payment</h1>
            <p className="mt-2 text-charcoal-600">If you were charged, please contact us at {COMPANY.phoneDisplay} and we'll sort it out right away.</p>
            <Link to="/book" className="mt-6 inline-block bg-primary text-white font-semibold px-6 py-3 rounded-md">Back to Booking</Link>
          </div>
        )}
        {state === "paid" && (
          <div className="bg-white border border-primary/30 rounded-md p-10" data-testid="booking-confirmed">
            <CheckCircle2 className="text-primary mx-auto mb-4" size={56} />
            <h1 className="text-3xl font-bold text-charcoal-900">Booking Confirmed!</h1>
            <p className="mt-2 text-charcoal-600">Thank you! A confirmation email is on its way to you.</p>
            {booking && (
              <div className="mt-6 text-left border border-gray-200 rounded-md divide-y divide-gray-100">
                <Row icon={CheckCircle2} label="Service" value={booking.service_name} />
                <Row icon={Calendar} label="Date" value={booking.date} />
                <Row icon={Clock} label="Time" value={booking.time_window} />
                <Row icon={MapPin} label="Address" value={booking.address} />
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-charcoal-500">Amount Paid</span>
                  <span className="font-semibold text-charcoal-900">${Number(booking.amount_paid || 0).toFixed(2)}</span>
                </div>
                {booking.balance > 0 && (
                  <div className="flex justify-between px-4 py-3 text-sm">
                    <span className="text-charcoal-500">Balance Due at Service</span>
                    <span className="font-semibold text-charcoal-900">${Number(booking.balance).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
            <p className="mt-5 text-xs text-charcoal-500">Need to reschedule or cancel? Call {COMPANY.phoneDisplay} at least 48 hours before your appointment.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 bg-charcoal-900 text-white font-semibold px-6 py-3 rounded-md">Back to Home <ArrowRight size={16} /></Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm">
      <Icon size={16} className="text-primary" />
      <span className="text-charcoal-500 w-24">{label}</span>
      <span className="font-medium text-charcoal-900">{value}</span>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const bid = params.get("booking_id");
  return (
    <div className="bg-[#f8f9fa] min-h-[70vh] flex items-center" data-testid="payment-cancel-page">
      <div className="ubm-container py-20 max-w-xl mx-auto text-center">
        <div className="bg-white border border-gray-200 rounded-md p-10">
          <XCircle className="text-charcoal-400 mx-auto mb-4" size={48} />
          <h1 className="text-2xl font-bold text-charcoal-900">Payment canceled</h1>
          <p className="mt-2 text-charcoal-600">No worries — your booking hasn't been confirmed and you were not charged. You can try again anytime.</p>
          <Link to="/book" className="mt-6 inline-block bg-primary text-white font-semibold px-6 py-3 rounded-md" data-testid="retry-booking">Return to Booking</Link>
        </div>
      </div>
    </div>
  );
}
